import type { PostureStatus } from '@/components/ui/posture-chip';
import type { SubmittalVerb } from '@/components/ui/verb-chip';
import type { EdlEntry } from '@/lib/api/deliverables';
import { daysFromToday } from './checkpoint-status';

/**
 * Status vocabulary for Contract → Deliverables (E06-F03). One derivation
 * shared by the table, the tiles, the filters and the drawer.
 *
 * Milestones use the same vocabulary (T-0229 / C2-18): the deliverables
 * service runs one submittal machine for every dated register row, and the
 * overdue scan already treats a milestone as dated work that can go late
 * and raise a missed-milestone deviation. The vendor reports a milestone
 * reached (submitted), IV&V accepts or rejects it, and accepting it is
 * what resolves that deviation, so a milestone needs the same next steps
 * as a deliverable rather than a read-only date state.
 */

type DeliverableStateKey =
  | 'accepted'
  | 'rejected'
  | 'awaiting-review'
  | 'revise'
  | 'overdue'
  | 'due-soon'
  | 'outstanding';

interface DeliverableState {
  key: DeliverableStateKey;
  verb: SubmittalVerb;
  /** One short line under the chip: timing or review stage. Empty when final. */
  detail: string;
}

export const NEEDS_ACTION: ReadonlySet<DeliverableStateKey> = new Set([
  'awaiting-review',
  'revise',
  'overdue',
  'due-soon',
]);

const DUE_SOON_DAYS = 14;

function timing(e: EdlEntry): string {
  if (e.expected_delivery_date) {
    const d = daysFromToday(e.expected_delivery_date);
    if (d < 0) return `${-d} ${d === -1 ? 'day' : 'days'} late`;
    if (d === 0) return 'due today';
    return `due in ${d} ${d === 1 ? 'day' : 'days'}`;
  }
  return e.due_text ? `SOW: ${e.due_text}` : 'no due date';
}

export function deliverableState(e: EdlEntry): DeliverableState {
  switch (e.status) {
    case 'approved':
      return { key: 'accepted', verb: 'approved', detail: '' };
    case 'approved_as_noted':
      return { key: 'accepted', verb: 'approved_as_noted', detail: '' };
    case 'rejected':
      return { key: 'rejected', verb: 'rejected', detail: '' };
    case 'submitted':
      return { key: 'awaiting-review', verb: 'submitted', detail: 'waiting for review' };
    case 'under_review':
      return { key: 'awaiting-review', verb: 'submitted', detail: 'under review' };
    case 'revise_resubmit':
      return { key: 'revise', verb: 'revise', detail: `with vendor · ${timing(e)}` };
    case 'overdue':
      return { key: 'overdue', verb: 'overdue', detail: timing(e) };
    default: {
      if (e.is_overdue) return { key: 'overdue', verb: 'overdue', detail: timing(e) };
      const d = e.expected_delivery_date ? daysFromToday(e.expected_delivery_date) : null;
      if (d !== null && d <= DUE_SOON_DAYS) {
        return { key: 'due-soon', verb: 'outstanding', detail: timing(e) };
      }
      return { key: 'outstanding', verb: 'outstanding', detail: timing(e) };
    }
  }
}

/**
 * Where a register row came from, in the words the Source column and the
 * drawer use: the change order that added it (C2-22), else how it was
 * authored.
 */
export function sourceWord(e: EdlEntry): string {
  if (e.source_change_order) return `From ${e.source_change_order.label}`;
  return e.origin === 'manually_authored' ? 'Added manually' : 'AI · no citation';
}

/**
 * Missing the facts a consultant needs to hold the vendor to the contract.
 * A milestone is held to its date; acceptance criteria are optional there.
 */
export function isMissingInfo(e: EdlEntry): boolean {
  const noDate = !e.expected_delivery_date && !e.due_text;
  if (e.item_type === 'milestone') return noDate;
  return noDate || e.acceptance_criteria.length === 0;
}

/**
 * Display order — the order of the contract (production issue 12):
 *   1. SOW reference in natural order (2.1.2.9 before 2.1.4.4, D-2 before
 *      D-10); referenced items before unreferenced ones.
 *   2. Position in the source document (cited page, then chunk ordinal),
 *      stored at approval; positioned items before unpositioned ones.
 *   3. Title.
 * Shared by the Deliverables page and the Baseline tab, so D-codes agree.
 */
export function compareDeliverables(a: EdlEntry, b: EdlEntry): number {
  if (a.sow_ref && b.sow_ref) {
    const byRef = a.sow_ref.localeCompare(b.sow_ref, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
    if (byRef !== 0) return byRef;
  } else if (a.sow_ref || b.sow_ref) {
    return a.sow_ref ? -1 : 1;
  }
  const byPosition = compareSourcePosition(a.source_position ?? null, b.source_position ?? null);
  if (byPosition !== 0) return byPosition;
  return a.title.localeCompare(b.title);
}

type SourcePosition = NonNullable<EdlEntry['source_position']>;

function compareSourcePosition(a: SourcePosition | null, b: SourcePosition | null): number {
  if (!a && !b) return 0;
  if (!a || !b) return a ? -1 : 1;
  const byPage = compareNullableNumber(a.page, b.page);
  if (byPage !== 0) return byPage;
  return compareNullableNumber(a.ordinal, b.ordinal);
}

/** Ascending; unknown values after known ones. */
function compareNullableNumber(a: number | null, b: number | null): number {
  if (a === null && b === null) return 0;
  if (a === null || b === null) return a === null ? 1 : -1;
  return a - b;
}

/** Dual-axis verification (E04-F06) in words. */
export function verificationLabel(e: EdlEntry): { posture: PostureStatus; label: string } {
  switch (e.rollup_color) {
    case 'green':
      return { posture: 'healthy', label: 'Verified' };
    case 'red':
      return { posture: 'critical', label: 'Failed' };
    case 'yellow_divergent':
      return { posture: 'at-risk', label: 'Divergent' };
    default:
      return {
        posture: 'unassessed',
        label: e.avero_verification === 'in_progress' ? 'Verifying' : 'Not verified',
      };
  }
}

const STATUS_WORD: Record<string, string> = {
  outstanding: 'Outstanding',
  submitted: 'Submitted',
  under_review: 'Under review',
  approved: 'Approved',
  approved_as_noted: 'Approved as noted',
  revise_resubmit: 'Revise & resubmit',
  rejected: 'Rejected',
  overdue: 'Overdue',
};

export function statusWord(status: string): string {
  return STATUS_WORD[status] ?? status;
}

/**
 * The button label for moving a deliverable to `to` from `from`. Terminal
 * outcomes (approve / approve as noted / reject) are confirmed in the drawer.
 */
export function transitionLabel(from: string, to: string): string {
  switch (to) {
    case 'submitted':
      return from === 'revise_resubmit' ? 'Mark resubmitted' : 'Mark submitted';
    case 'under_review':
      return 'Start review';
    case 'approved':
      return 'Approve';
    case 'approved_as_noted':
      return 'Approve as noted';
    case 'revise_resubmit':
      return 'Send back for revision';
    case 'rejected':
      return 'Reject';
    case 'overdue':
      return 'Mark overdue';
    default:
      return statusWord(to);
  }
}

export const TERMINAL_TARGETS: ReadonlySet<string> = new Set([
  'approved',
  'approved_as_noted',
  'rejected',
]);
